import React, { FC, ReactNode, useContext } from "react";
import { format } from "date-fns";
import { LocalizationContext } from "v3/context/localization";
import { Button } from "@pepdirect/v3/ui/Button";
import { Heading } from "@pepdirect/v3/ui/Heading";
import { TemplateAsComponent } from "@pepdirect/v3/ui/TemplateAsComponent";
import { CheckCircledIconSvg, GiftIconSvg } from "@pepdirect/v3/ui/icons";
import { RequestedDeliveryDateSummary } from "v3/components/orderDetails/RequestedDeliveryDateSummary";
import { AddressDetails } from "v3/components/orderDetails/AddressDetails";
import { PaymentDetails } from "v3/components/orderDetails/PaymentDetails";
import { Fulfillments } from "v3/components/orderDetails/OrderSummary/Fulfillments";
import {
  ContactInfo,
  CustomText,
  GiftInfo,
  SubmittedOrderQuery,
  SubmittedOrderType,
} from "v3/services/graphql/generated";
import { PaymentDisplayDetails } from "v3/types/payment";
import { parseTemplateAsString } from "@pepdirect/v3/helpers/localization";
import { colors } from "@pepdirect/v3/styles/variables";
import {
  StyledBoldText,
  StyledDetailItem,
  StyledDetails,
  StyledEmail,
  StyledGiftConfirmationEmail,
  StyledGiftMessage,
  StyledOrderSpecs,
  StyledSection,
  StyledSectionWrapper,
  StyledSpacer,
  StyledNote,
  StyledExternalLink,
} from "./index.styled";
import { TenantContext } from "v3/context/tenant";

interface OrderSpecsProps {
  contactInfo: ContactInfo;
  editPayment?: () => void;
  editShip?: () => void;
  fulfillments?: NonNullable<
    SubmittedOrderQuery["submittedOrder"]
  >["fulfillments"];
  giftInfo?: GiftInfo | null;
  isBillingEqualShipping: boolean;
  shippingMethodName?: string;
  submittedDate?: Date | null;
  paymentDetails: PaymentDisplayDetails;
  poNumber?: string | null;
  requestedDeliveryDate?: string | null;
  isExternalOrder?: boolean | null;
  orderType?: SubmittedOrderType | null;
}

export const OrderSpecs: FC<OrderSpecsProps> = ({
  contactInfo,
  editPayment,
  editShip,
  fulfillments,
  giftInfo,
  isBillingEqualShipping,
  shippingMethodName,
  submittedDate,
  paymentDetails,
  poNumber,
  requestedDeliveryDate = null,
  isExternalOrder,
  orderType,
}) => {
  const { localization } = useContext(LocalizationContext);
  const { tenant } = useContext(TenantContext);

  const isReviewStep = !submittedDate;
  const { email, phone, shippingAddress } = contactInfo;

  const isCylinderExchange =
    orderType === SubmittedOrderType.MissingGasExchangeCylinder;
  const {
    shippingHeadingText: shippingHeadingTextReview = "Shipping Options",
    giftShippingHeadingText = "Recipient Shipping Options",
    editButtonText = "Edit",
    emailNote = "Confirmation email will be sent to ${userEmail}",
    giftEmailNote = "Gift email will be sent to ${gifteeEmail}",
    giftMessageNote = "with the following gift message:",
    paymentHeadingText: paymentHeadingTextReview = "Payment Options",
  } = localization?.checkout?.reviewStep || {};
  const {
    shippingHeadingText: shippingHeadingTextStatus = "Shipping Info",
    orderDateText = "Order placed",
    paymentHeadingText: paymentHeadingTextStatus = "Payment Info",
  } = localization?.checkout?.orderStatusPage || {};
  const {
    poNumberText = "Purchase order number",
    shippingMethodText = "Shipping via ${shippingMethodName}",
  } = localization?.checkout?.orderDetails || {};
  const { yourRequestedDateText = "Your requested delivery date is" } =
    localization?.checkout?.rdd || {};

  if (!shippingAddress) return null;

  const shipHeading = () => {
    if (isReviewStep)
      return giftInfo ? giftShippingHeadingText : shippingHeadingTextReview;
    return shippingHeadingTextStatus;
  };

  const cylinderExchangeText = tenant?.brandConfig.customTexts.find((info) => {
    return (
      info.location === "orderDetails" &&
      info.type === "missingGasExchangeCylinder"
    );
  });

  const getMap = (term: CustomText) => {
    return term.links.reduce<Record<string, ReactNode>>((links, link) => {
      const { linkValue, key, displayValue } = link;
      links[key] = (
        <StyledExternalLink href={linkValue} underline>
          {displayValue}
        </StyledExternalLink>
      );
      return links;
    }, {});
  };

  return (
    <StyledOrderSpecs isReviewStep={isReviewStep}>
      {!isExternalOrder && !isReviewStep && (
        <StyledDetails isReviewStep={isReviewStep}>
          <StyledDetailItem>
            <div>{orderDateText}</div>
            <div>{format(new Date(submittedDate), "P")}</div>
          </StyledDetailItem>

          {poNumber && (
            <StyledDetailItem>
              <StyledBoldText>{poNumberText}</StyledBoldText> {poNumber}
            </StyledDetailItem>
          )}

          {isCylinderExchange && cylinderExchangeText && (
            <StyledNote>
              {cylinderExchangeText.body.map((text) => (
                <TemplateAsComponent
                  template={text}
                  key={text}
                  map={getMap(cylinderExchangeText)}
                />
              ))}
            </StyledNote>
          )}
          {!isCylinderExchange && <Fulfillments fulfillments={fulfillments} />}
        </StyledDetails>
      )}

      {!isCylinderExchange && (
        <>
          <StyledSectionWrapper>
            {isReviewStep && <CheckCircledIconSvg color={colors.green} />}
            <Heading size={isReviewStep ? "s" : "xs"} level="h3">
              {shipHeading()}
            </Heading>
          </StyledSectionWrapper>

          <StyledSectionWrapper>
            {isReviewStep && <StyledSpacer />}
            <StyledDetails isReviewStep={isReviewStep}>
              <StyledSection>
                <AddressDetails address={shippingAddress} phone={phone} />
                {isReviewStep && (
                  <Button
                    type="text"
                    data-testid="review-section-edit-ship"
                    onClick={editShip}
                  >
                    {editButtonText}
                  </Button>
                )}
              </StyledSection>

              {shippingMethodName && (
                <div>
                  {parseTemplateAsString(shippingMethodText, {
                    shippingMethodName,
                  })}
                </div>
              )}

              {requestedDeliveryDate && (
                <RequestedDeliveryDateSummary
                  yourRequestedDateText={yourRequestedDateText}
                  requestedDeliveryDate={requestedDeliveryDate}
                />
              )}

              {isReviewStep && (
                <div>
                  <TemplateAsComponent
                    template={emailNote}
                    map={{
                      userEmail: <StyledEmail>{email}</StyledEmail>,
                    }}
                  />
                </div>
              )}

              {isReviewStep && giftInfo && (
                <div>
                  <StyledGiftConfirmationEmail>
                    <GiftIconSvg />
                    <TemplateAsComponent
                      template={giftEmailNote}
                      map={{
                        gifteeEmail: (
                          <StyledEmail>{giftInfo.gifteeEmail}</StyledEmail>
                        ),
                      }}
                    />
                    {giftInfo.gifteeMessage && ` ${giftMessageNote}`}
                  </StyledGiftConfirmationEmail>
                  {giftInfo.gifteeMessage && (
                    <StyledGiftMessage>
                      {giftInfo.gifteeMessage}
                    </StyledGiftMessage>
                  )}
                </div>
              )}
            </StyledDetails>
          </StyledSectionWrapper>
        </>
      )}

      {!isExternalOrder && (
        <>
          <StyledSectionWrapper>
            {isReviewStep && <CheckCircledIconSvg color={colors.green} />}
            <Heading size={isReviewStep ? "s" : "xs"} level="h3">
              {isReviewStep
                ? paymentHeadingTextReview
                : paymentHeadingTextStatus}
            </Heading>
          </StyledSectionWrapper>

          <StyledSectionWrapper>
            {isReviewStep && <StyledSpacer />}
            <StyledDetails isReviewStep={isReviewStep}>
              <StyledSection>
                <PaymentDetails
                  isBillingEqualShipping={isBillingEqualShipping}
                  paymentDetails={paymentDetails}
                  isReviewStep={isReviewStep}
                />
                {isReviewStep && (
                  <Button
                    type="text"
                    data-testid="review-section-edit-payment"
                    onClick={editPayment}
                  >
                    {editButtonText}
                  </Button>
                )}
              </StyledSection>

              {isReviewStep && poNumber && (
                <div>
                  <StyledBoldText>{poNumberText}</StyledBoldText> {poNumber}
                </div>
              )}
            </StyledDetails>
          </StyledSectionWrapper>
        </>
      )}
    </StyledOrderSpecs>
  );
};
